import { useCallback } from "react";
import axios from "axios";
import { useSnackbar } from "../Component/context/SnackbarContext";
import { useLanguage } from "../Component/context/Languagecontext";

/**
 * useRandomContent
 *
 * A shared hook that fetches a random challenge/content suggestion from the API.
 * Supports category and optional subCategory for components that need it.
 *
 * @returns {{ fetchRandomContent: (options) => Promise<string|null> }}
 *
 * Usage:
 *   const { fetchRandomContent } = useRandomContent();
 *
 *   // Basic (no subCategory):
 *   const content = await fetchRandomContent({ category: "challenge" });
 *
 *   // With subCategory:
 *   const content = await fetchRandomContent({ category: "dare", subCategory: "mild" });
 *
 *   // Override lanText (optional – hook reads it from context by default):
 *   const content = await fetchRandomContent({ category: "truth", lanText: "ar" });
 */
const useRandomContent = () => {
    const { lanText, t } = useLanguage();
    const { showSnackbar } = useSnackbar();

    /**
     * fetchRandomContent
     *
     * @param {object}  options
     * @param {string}  options.category       - Required. Sent to the API to scope the content pool.
     * @param {string}  [options.subCategory]  - Optional. For components that have sub-categories.
     * @param {string}  [options.lanText]      - Optional override; falls back to context lanText.
     * @param {Function} [options.onSuccess]   - Optional callback receiving the cleaned content string.
     * @param {Function} [options.onError]     - Optional callback for error handling.
     *
     * @returns {Promise<string|null>}  The cleaned content string, or null on failure.
     */
    const fetchRandomContent = useCallback(
        async ({
            category,
            subCategory,
            lanText: lanTextOverride,
            onSuccess,
            onError,
        } = {}) => {
            const resolvedLanText = lanTextOverride || lanText || "en";

            try {
                const payload = {
                    lanText: resolvedLanText,
                    category,
                    ...(subCategory ? { subCategory } : {}),
                };

                const res = await axios.post(
                    "https://api.lolcards.link/api/web/content",
                    payload
                );

                if (res.data.status === 1) {
                    // Strip any <b> / </b> tags returned by the API
                    const cleanContent = res.data.data.Content.replace(/<\/?b>/gi, "");

                    onSuccess?.(cleanContent);
                    return cleanContent;
                }

                return null;
            } catch (error) {
                console.error("useRandomContent – API Error:", error);
                const errorMsg = t?.contentApiFail || "Content not available.";
                showSnackbar(errorMsg, "error");
                onError?.(error);
                return null;
            }
        },
        [lanText, t, showSnackbar]
    );

    return { fetchRandomContent };
};

export default useRandomContent;